import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, Plus, ArrowRight, SignOut, ShieldCheck, Books, Trash, Copy, CreditCard, Stack, Gauge, ArrowUp, ArrowDown, Users, Megaphone, Info, CurrencyDollar, Heart, EyeSlash, UserCircle, Compass, Wallet, Tray, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser, clearAuth } from "@/lib/api";
import { copyText } from "@/lib/io";
import CreateSiloDialog from "@/components/CreateSiloDialog";
import UsageOverview from "@/components/UsageOverview";
import { SiloKindPill } from "@/components/SiloKindPill";
// Jun 23, 2026 — CreatorLLMHeaderCard (all-silos → Pro banner) removed from the
// dashboard per Randy; the per-silo Ops tab covers it.
// May 28, 2026 — KeyBalanceCard import removed; replaced by ProviderTrackersCard.
import ProviderTrackersCard from "@/components/ProviderTrackersCard";
import CostCacheDiag from "@/components/CostCacheDiag";
import CommunicationsPanel from "@/components/CommunicationsPanel";
import MarketingPanel from "@/components/MarketingPanel";
import HowItWorksFAQv2 from "@/components/HowItWorksFAQv2";
import { TopUpModal, YouTubeKeyCard } from "@/components/BillingControls";
import CreatorProviderByokPanel from "@/components/CreatorProviderByokPanel";
import BYOKPromoBanner from "@/components/BYOKPromoBanner";
import PlatformKeyInfoCard from "@/components/PlatformKeyInfoCard";
import CreatorLLMRoutingCard from "@/components/CreatorLLMRoutingCard";
import UsersTab from "@/components/UsersTab";
import InterestsPicker from "@/components/InterestsPicker";
import EarningsDashboard from "@/components/EarningsDashboard";
import { SiteNameCard, ProfileCard } from "@/pages/AccountSettings";
import AffiliateReturnHandler from "@/components/AffiliateReturnHandler";
import CompAlertBanner from "@/components/CompAlertBanner";
import IdentityPill from "@/components/IdentityPill";
import SiloAlertsDot from "@/components/SiloAlertsDot";
import FinishSetupPill from "@/components/FinishSetupPill";
import VoucherBackupBanner from "@/components/VoucherBackupBanner";
import GracePeriodBanner from "@/components/GracePeriodBanner";
import TrialRibbon from "@/components/TrialRibbon";
import InvitesBanner from "@/components/InvitesBanner";
import KeyFailureBanner from "@/components/KeyFailureBanner";
import VoiceRequestBanner from "@/components/VoiceRequestBanner";
// Silo Wars add-on — battle invite inbox card. Auto-hides when the
// inbox is empty so quiet dashboards stay quiet.
import BattleInboxCard from "@/features/battles/BattleInboxCard";
import BattleInboxBadge from "@/features/battles/BattleInboxBadge";
import { useBattlesEnabled } from "@/features/battles/useBattlesEnabled";
import NewLLMBanner from "@/components/NewLLMBanner";
import { useSiloCubeEnabled } from "@/lib/siloCube";
import { SiloCubeCard } from "@/components/SiloCubeCard";
import SiloImageStudio from "@/components/SiloImageStudio";
import SiloCreditChip, { LOW_CREDIT_CENTS } from "@/components/SiloCreditChip";
import { useSiloArt, SiloArtBand, SiloArtCanvas, SiloArtModeSlider } from "@/components/SiloArtLayer";
import { CHROME_FACE_BY_TAB } from "@/lib/siloArt";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const user = getUser();
  const nav = useNavigate();
  const location = useLocation();
  const { enabled: battlesEnabled } = useBattlesEnabled();
  // Creator Pro gate for the Silo Wars entry pill (Jul 2026, Randy) — an
  // affiliate who hasn't bought the silo_wars SKU sees the frozen shimmer and a
  // click opens the buffet, instead of navigating into the arena.
  const { locked: cpLocked, lockStyle: cpLockStyle } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();
  const warsLocked = cpLocked("silo_wars");
  const [silos, setSilos] = useState([]);
  const [pendingCounts, setPendingCounts] = useState({});
  // Feb 2026 pt89h (Randy) — chore-confirmation counts (separate from
  // Staging-approval counts above). Powers the tiny in-canvas dot on
  // each organizer-silo card so the owner is visually alerted when a
  // kid has proposed a chore completion.
  const [choreConfirmCounts, setChoreConfirmCounts] = useState({});
  // Feb 24, 2026 (Randy — YTRE activity alert system) — per-silo YTRE
  // unread state. Shape: { silo_id: { unread_urls: N, total_new_replies: M } }.
  // Fetched once on mount + polled every 60s. Combined with
  // choreConfirmCounts inside SiloAlertsDot to produce the aggregate
  // dot count on each silo card. Cheap Mongo aggregation on the backend,
  // no YouTube API calls — those only fire when the user actively hits
  // the /activity/scan endpoint (which the YTRE tab does).
  const [ytreActivityCounts, setYtreActivityCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [usageVersion, setUsageVersion] = useState(0);
  const [me, setMe] = useState(user);
  const [heldCents, setHeldCents] = useState(0);
  // Jul 2026 (Randy) — per-silo AI Credit chip + low-balance surfacing.
  const [voucherSummary, setVoucherSummary] = useState(null);
  const [creditTopupSiloId, setCreditTopupSiloId] = useState(null);
  // Feb 20, 2026 — Honor ?tab=X from the URL on first load AND on every
  // route change. Previously sessionStorage took precedence, so deep
  // links like "See your referral base →" silently failed.
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("tab");
    return fromUrl || sessionStorage.getItem("dashboard_tab") || "silos";
  });
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get("tab");
    if (fromUrl && fromUrl !== activeTab) setActiveTab(fromUrl);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // Oct 3 2026 — a `pricing.affiliate_base_rev_share_pct ?? 25` lived here
  // and was read by nothing. Removed: a remembered price with no consumer is
  // the easiest kind to leave behind and the hardest kind to notice.
  const { enabled: cubeEnabled } = useSiloCubeEnabled();
  const { setActiveFace: setArtFace, activeFace } = useSiloArt();
  useEffect(() => { setArtFace(CHROME_FACE_BY_TAB[activeTab] || "wars"); }, [activeTab, setArtFace]);
  // Always open the dashboard at the very top (iOS Safari sometimes restores a
  // mid-scroll position, which tangles the hero band under the sticky header).
  useEffect(() => {
    try { if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual"; } catch { /* */ }
    window.scrollTo(0, 0);
  }, []);
  const [studioSilo, setStudioSilo] = useState(null);
  const openStudio = (s) => setStudioSilo(s);

  // Identity dropdown "Silo Art" shortcut → open the studio for the chosen silo
  // at the chosen stage. Works whether we just navigated here (effect re-runs
  // when silos load) or were already on the dashboard (window event).
  useEffect(() => {
    const openFromSession = () => {
      let raw;
      try { raw = sessionStorage.getItem("mysilo_open_studio"); } catch { return; }
      if (!raw) return;
      let p;
      try { p = JSON.parse(raw); } catch { return; }
      if (!p || !p.siloId) return;
      const s = (silos || []).find((x) => x.id === p.siloId);
      if (!s) return; // silos not loaded yet — re-runs when they are
      try {
        if (p.stage) sessionStorage.setItem("mysilo_studio_stage", p.stage);
        sessionStorage.removeItem("mysilo_open_studio");
      } catch { /* */ }
      setStudioSilo(s);
    };
    openFromSession();
    window.addEventListener("mysilo:open-studio", openFromSession);
    return () => window.removeEventListener("mysilo:open-studio", openFromSession);
  }, [silos]);

  const switchTab = (id) => {
    setActiveTab(id);
    sessionStorage.setItem("dashboard_tab", id);
  };

  const load = async () => {
    setLoading(true);
    // May 25, 2026 — Auto-retry once on transient network/proxy errors.
    // Backend hot-reload during active development causes a ~3s window
    // where /api/* returns 502. Without this retry, the user sees a
    // "Failed to load silos" toast even though the very next request
    // would succeed. We only retry on network/5xx — 401 still escalates
    // to login redirect on the first attempt.
    const attempt = async () => {
      const [silosRes, meRes, vsRes] = await Promise.all([
        api.get("/silos"),
        api.get("/auth/me").catch(() => null),
        api.get("/me/voucher-summary").catch(() => null),
      ]);
      setSilos(silosRes.data);
      if (vsRes?.data) { setHeldCents(Number(vsRes.data.held_topup_cents) || 0); setVoucherSummary(vsRes.data); }
      if (meRes?.data) {
        setMe(meRes.data);
        setUser(meRes.data);
      }
    };
    try {
      await attempt();
    } catch (e) {
      if (e.response?.status === 401) { clearAuth(); nav("/login"); return; }
      // Network error OR 5xx — wait 1.5s and try ONCE more.
      const isTransient = !e.response || (e.response.status >= 500 && e.response.status < 600);
      if (isTransient) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          await attempt();
          return;
        } catch (e2) {
          if (e2.response?.status === 401) { clearAuth(); nav("/login"); return; }
        }
      }
      toast.error("Failed to load silos");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Jul 2026 (Randy) — per-silo "N waiting for approval" badge counts.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/calendar/pending-counts");
        setPendingCounts(r.data?.counts || {});
      } catch { /* silent */ }
    })();
  }, [silos.length]);

  // Feb 2026 pt89h (Randy) — chore-confirmations summary. Polls every
  // 30s so the dot appears without a page refresh when a kid taps.
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await api.get("/calendar/pending-confirmations-summary");
        setChoreConfirmCounts(r.data?.summary || {});
      } catch { /* silent */ }
    };
    fetch();
    const t = setInterval(fetch, 30_000);
    return () => clearInterval(t);
  }, [silos.length]);

  // Feb 24, 2026 (Randy — YTRE activity alerts) — same 30-60s polling
  // pattern as choreConfirmCounts, but hitting the YTRE summary route.
  // Cheap Mongo-only aggregation on the backend; no YouTube API cost
  // per poll. Actual scans (which DO cost YouTube quota) happen only
  // when the user opens a YTRE tab.
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await api.get("/ytre/activity/summary");
        setYtreActivityCounts(r.data?.summary || {});
      } catch { /* silent */ }
    };
    fetch();
    // Oct 2026 (Randy) — arriving here sweeps EVERY silo that has a channel
    // connected, instead of only refreshing whichever silo he later opens.
    // Fire-and-forget: the backend returns at once and scans in the
    // background, so re-read the summary a few seconds later to pick it up.
    let settle;
    api.post("/ytre/activity/sweep")
      .then((r) => { if (r.data?.queued) settle = setTimeout(fetch, 12_000); })
      .catch(() => { /* silent */ });
    const t = setInterval(fetch, 60_000);
    return () => { clearInterval(t); if (settle) clearTimeout(settle); };
  }, [silos.length]);

  // Jun 18, 2026 — Confirm-on-return for voucher top-ups. Stripe webhooks are
  // NOT delivered to this origin, so without this the creator pays $5 and the
  // voucher never fills. On returning from Stripe (?voucher_topup=success&
  // session_id=…) we land the credit immediately, then refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("voucher_topup") !== "success") return;
    const sessionId = params.get("session_id");
    // Strip the params right away so this can't re-fire on re-render.
    window.history.replaceState({}, "", window.location.pathname);
    if (!sessionId) { toast.success("Top-up complete — your voucher is updating…"); return; }
    (async () => {
      try {
        const r = await api.post("/stripe/voucher/confirm", { session_id: sessionId });
        if (r.data?.credited) {
          const amt = r.data.amount_cents ? `$${(r.data.amount_cents / 100).toFixed(2)}` : "Your top-up";
          if (r.data.held) {
            toast.success(`${amt} added — held safely. It lands in your first silo the moment you create one.`);
          } else {
            toast.success(`${amt} added to your voucher!`);
          }
          setTimeout(() => window.location.reload(), 1600);
        } else {
          toast("Payment is still processing — it'll appear shortly.", { icon: "⏳" });
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't confirm your top-up — it may still land shortly.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jun 2026 — Confirm-on-return for the $300 affiliate gate. Webhooks aren't
  // delivered to this origin, so without this the user pays the gate and never
  // gets promoted (the bug that left a real creator stuck 'pending'). On
  // returning (?affiliate_session=…) we run the promotion immediately.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("affiliate_session");
    if (!sessionId) return;
    window.history.replaceState({}, "", window.location.pathname);
    (async () => {
      try {
        const r = await api.post("/stripe/affiliate-gate/confirm", { session_id: sessionId });
        if (r.data?.activated) {
          toast.success("🎯 You're now a Creator Pro!");
          setTimeout(() => window.location.reload(), 1600);
        } else {
          toast("Payment is still processing — your Creator Pro status will appear shortly.", { icon: "⏳" });
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't confirm your Creator Pro upgrade — it may still land shortly.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jun 2026 (Randy) — self-heal a paid-but-uncredited voucher top-up. The
  // confirm-on-return is fire-once; a fast affiliate signup that jumped
  // straight to the $300 gate could interrupt it, leaving the money paid at
  // Stripe but never credited (Reast's $10). On dashboard load we sweep Stripe
  // once per tab and land any orphaned top-up. Idempotent server-side.
  useEffect(() => {
    if (!me) return;
    try {
      if (sessionStorage.getItem("voucher_heal_done") === "1") return;
      sessionStorage.setItem("voucher_heal_done", "1");
    } catch { /* ignore */ }
    (async () => {
      try {
        const r = await api.post("/stripe/voucher/heal-uncredited", {});
        if ((r.data?.healed_cents || 0) > 0) {
          const vs = await api.get("/me/voucher-summary").catch(() => null);
          if (vs?.data) setHeldCents(Number(vs.data.held_topup_cents) || 0);
          toast.success(`Recovered $${(r.data.healed_cents / 100).toFixed(2)} from an earlier payment — it's credited and held for your first silo.`);
        }
      } catch { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // iter-31-P2b/c: honor the "dashboard_scroll_to" session flag AND the
  // custom window event fired by the clickable key-source pill in usage
  // reports. Fixes the bug where a pill click on /dashboard (already there)
  // did nothing because nav('/dashboard#hash') is a no-op for React Router.
  useEffect(() => {
    if (loading || !me) return;
    const goToHash = (hash, tabName) => {
      if (tabName) setActiveTab(tabName);
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    };
    // Cross-route arrival: sessionStorage-based handoff from another page.
    try {
      const hash = sessionStorage.getItem("dashboard_scroll_to");
      const tab = sessionStorage.getItem("dashboard_tab");
      if (hash) {
        sessionStorage.removeItem("dashboard_scroll_to");
        goToHash(hash, tab);
      }
    } catch { /* ignore */ }
    // Same-route dispatch: pill clicked while already on /dashboard.
    const onEvent = (e) => {
      const { hash, tab } = (e.detail || {});
      // iter-35a: support tab-only navigation (no hash)
      if (tab && !hash) { setActiveTab(tab); return; }
      if (hash) goToHash(hash, tab);
    };
    window.addEventListener("dashboard-scroll-to", onEvent);
    return () => window.removeEventListener("dashboard-scroll-to", onEvent);
  }, [loading, me]);

  const logout = () => { clearAuth(); nav("/login"); };

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/s/${slug}`;
    const ok = await copyText(url);
    toast[ok ? "success" : "error"](ok ? "Public silo link copied." : "Could not copy. Try selecting the link and pressing ⌘C.");
  };

  const onDelete = async (silo) => {
    if (!window.confirm(`Delete "${silo.name}" and all its documents? This cannot be undone.`)) return;
    try {
      await api.delete(`/silos/${silo.id}`);
      toast.success("Silo deleted.");
      setSilos((s) => s.filter((x) => x.id !== silo.id));
    } catch { toast.error("Delete failed"); }
  };

  // Sprint Feb 7, 2026 — toggle hide-from-public. When on, /s/{slug} 404s
  // for the world but the owner can still manage it normally.
  const toggleHide = async (silo) => {
    const next = !silo.is_hidden_from_public;
    try {
      const r = await api.patch(`/silos/${silo.id}`, { is_hidden_from_public: next });
      setSilos((arr) => arr.map((s) => (s.id === silo.id ? r.data : s)));
      toast.success(next ? "Silo is now hidden from the public." : "Silo is public again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change visibility.");
    }
  };

  // Reorder helpers — move a silo up/down in the user's list and persist the
  // new order on the server. The backend assigns sort_order = 0..N-1 in the
  // submitted sequence, so the order survives logout / multiple devices.
  const moveSilo = async (idx, direction) => {
    // Reorder only OWNED silos (collaborations are a separate section below and
    // can't be reordered by a collaborator — /silos/order is owner-gated).
    const owned = silos.filter((s) => s.owner_user_id === user?.id);
    const collab = silos.filter((s) => s.owner_user_id !== user?.id);
    const target = idx + direction;
    if (target < 0 || target >= owned.length) return;
    const next = owned.slice();
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setSilos([...next, ...collab]);
    try {
      await api.post("/silos/order", { silo_ids: next.map((s) => s.id) });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save new order.");
      setSilos(silos);  // revert
    }
  };

  // Jun 2026 (Randy) — split owned silos from collaborations so each gets its
  // own dashboard section. A silo is "collaborative" for this user when they
  // aren't its owner (they were invited via collaborator_ids).
  const ownedSilos = silos.filter((s) => s.owner_user_id === user?.id);
  const collabSilos = silos.filter((s) => s.owner_user_id !== user?.id);

  // Jul 2026 (Randy) — AI Credit: map silo_id → its three voucher pools, and
  // the creator's account-level BYOK status (a provider on the creator's own
  // key is never "low" — it doesn't lean on platform vouchers).
  const byokStatus = voucherSummary?.byok_status || {};
  const creditBySilo = {};
  for (const v of (voucherSummary?.silos || [])) {
    creditBySilo[v.silo_id] = {
      gemini: v.gemini_voucher_cents || 0,
      openai: v.openai_voucher_cents || 0,
      anthropic: v.anthropic_voucher_cents || 0,
      ghost: v.ghost_by_provider || {},
    };
  }
  const isProviderLow = (cents, prov) => !byokStatus[prov] && Number(cents || 0) < LOW_CREDIT_CENTS;
  const lowSilos = ownedSilos.filter((s) => {
    const b = creditBySilo[s.id];
    return b && (isProviderLow(b.gemini, "gemini") || isProviderLow(b.openai, "openai") || isProviderLow(b.anthropic, "anthropic"));
  });
  // Dismissed for this browser session only — read once on mount so a tab
  // switch or a route change doesn't bring it back mid-session.
  const [lowCreditDismissed, setLowCreditDismissed] = useState(() => {
    try { return sessionStorage.getItem("mysilo_low_credit_dismissed") === "1"; } catch { return false; }
  });

  return (
    <div className="min-h-screen bg-[#FDFCF8] overflow-x-clip relative isolate" data-testid="dashboard-page">
      <SiloArtCanvas />
      <AffiliateReturnHandler />
      {/* Feb 16 2026 (Randy pt-comp-grant-system) — Top banner alerts the user
          when their monthly subscription is required (dues toggle flipped on
          or comp expired/revoked). Non-dismissible; tapping opens Stripe. */}
      <CompAlertBanner />
      <header className="border-b hairline bg-[#FDFCF8]/95 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center"><Sparkle size={18} weight="duotone" /></div>
            <div>
              <div className="font-serif-display text-xl leading-none">My Silo</div>
              <div className="text-[10px] text-[#6B6A68] tracking-[0.25em] uppercase mt-1">Creator dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Jul 2026 (Randy) — Account & Billing / Super Admin / Sign out
                moved INTO the identity dropdown (below) to clean up the top bar,
                especially on mobile. */}
            <BattleInboxBadge />
            <IdentityPill />
          </div>
        </div>
        {/* Jul 2026 (Randy) — tabs fused to the TOP command header as one block,
            matching the silo-manage header+tabs look (not the hero art band). */}
        <div className="max-w-6xl mx-auto px-2 sm:px-6 border-t hairline">
          <div className="flex items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="dashboard-tabs">
            <button onClick={() => switchTab("silos")} data-testid="dashboard-tab-silos"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "silos" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <Stack size={16} weight="duotone" /> Your Silos
            </button>
            <button onClick={() => switchTab("users")} data-testid="dashboard-tab-users"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "users" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <Users size={16} weight="duotone" /> Users
            </button>
            <button onClick={() => switchTab("earnings")} data-testid="dashboard-tab-earnings"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "earnings" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <CurrencyDollar size={16} weight="duotone" /> Your Earnings
            </button>
            <button onClick={() => switchTab("profile")} data-testid="dashboard-tab-profile"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "profile" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <UserCircle size={16} weight="duotone" /> Profile
            </button>
            <button onClick={() => switchTab("interests")} data-testid="dashboard-tab-interests"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "interests" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <Compass size={16} weight="duotone" /> Interests
            </button>
            <button onClick={() => switchTab("ops")} data-testid="dashboard-tab-ops"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "ops" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <Gauge size={16} weight="duotone" /> Operations
            </button>
            <button onClick={() => switchTab("comms")} data-testid="dashboard-tab-comms"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "comms" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <Megaphone size={16} weight="duotone" /> Marketing &amp; Com
            </button>
            <button onClick={() => switchTab("faq")} data-testid="dashboard-tab-faq"
              className={`px-4 sm:px-5 py-3 text-sm flex items-center gap-2 border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${activeTab === "faq" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              <Info size={16} weight="duotone" /> How it works
            </button>
          </div>
        </div>
      </header>

      <main className="siloart-copy max-w-6xl mx-auto px-6 py-12 relative z-10">
        <GracePeriodBanner user={user} />
        <TrialRibbon />
        <InvitesBanner />
        <KeyFailureBanner />
        <VoiceRequestBanner />
        <BattleInboxCard />
        <FinishSetupPill />
        <VoucherBackupBanner />
        {/* Feb 19, 2026 (Randy Photo #4, iter2) — always paint a solid cream
            card. The SiloArtBand (rendered as `absolute inset-0 z-0`) sits
            ON TOP of this bg when V2 art has real leader-silo pigment; when
            there's no leader silo yet, or mode is v1/off, the cream shows
            through instead of the previous transparent → murky-gray fall-
            through. */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="isolate relative overflow-hidden rounded-2xl px-5 py-6 sm:px-6 sm:py-7 flex items-end justify-between flex-wrap gap-4 bg-[#FDFCF8] border hairline" data-testid="dashboard-hero-card">
          <SiloArtBand faceId={activeFace} />
          <div className="relative z-10">
            <h1 className="font-serif-display text-4xl sm:text-5xl !text-black silo-loose">
              Hello, <em className="!text-black" data-testid="dashboard-hero-name">{user?.name?.split(" ")[0] || "creator"}</em>.
            </h1>
            <p className="mt-2 !text-black">Manage your silos. Each one is an isolated digital twin.</p>
            {/* Sprint Feb 8, 2026 — quick site-name editor inline on the
                dashboard. The full editor lives in Account Settings →
                "Site name" card; this is the same field exposed here so
                you can change your platform handle without leaving the
                dashboard. */}
            <SiteNameInline />
            <SiloArtModeSlider />
          </div>
          <div className="flex items-stretch gap-2 relative z-10">
            {/* iter-35e: every user — including creators — has a fan portal.
                It surfaces the silos they're subscribed to, their bookmarks
                and tips. Lets a creator also be a fan of other creators.
                Jul 2026 (Randy) — pills unified to px-4 py-3 text-sm so Fan
                Portal / Silo Wars / New silo all share one size. */}
            <Link to="/fan-portal" data-testid="dashboard-view-fan-portal"
              className="px-4 py-3 rounded-md bg-white border hairline text-sm text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] transition-colors flex items-center gap-2">
              <Heart size={16} weight="duotone" /> <span className="hidden sm:inline">View my fan portal</span><span className="sm:hidden">Fan portal</span>
            </Link>
            {/* Silo Wars — visible to creators/affiliates; access gated by the
                silo_wars SKU. Locked users get the frozen shimmer + buffet. */}
            {battlesEnabled && (user?.is_affiliate || user?.role === "super_admin") && (
              <LockGate locked={warsLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("silo_wars")}>
                <Link to="/wars" data-testid="dashboard-silo-wars-link"
                  className="px-4 py-3 rounded-md bg-[#1C1B1A] border border-[#A89360]/40 text-sm text-[#A89360] hover:bg-[#2C2B2A] hover:border-[#A89360] transition-colors flex items-center gap-2">
                  <span className="text-base leading-none">⚔</span> <span className="hidden sm:inline">Silo Wars</span><span className="sm:hidden">Wars</span>
                </Link>
              </LockGate>
            )}
            <button onClick={() => setShowCreate(true)} data-testid="create-silo-btn"
              className="px-4 py-3 rounded-md bg-[#1C1B1A] text-sm text-[#FDFCF8] hover:bg-[#3A4A3E] transition-colors flex items-center gap-2 shadow-sm">
              <Plus size={16} weight="bold" /> New silo
            </button>
          </div>
        </motion.div>

        <section className="mt-8">

          {/* === SILOS TAB === */}
          <div className={activeTab === "silos" ? "" : "hidden"} data-testid="dashboard-pane-silos">
            <div className="mb-6">
              <h2 className="font-serif-display text-2xl text-[#1C1B1A] silo-loose">Your silos.</h2>
              <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl silo-loose">
                Each silo is a sovereign, isolated AI twin — its own corpus, its own voice, its own public link. They never bleed into each other.
              </p>
            </div>

            {heldCents > 0 && (
              <div
                className="mb-6 rounded-xl border border-[#4A5D4E]/40 bg-[#E8EBE5]/60 px-4 py-3.5 flex items-start gap-3"
                data-testid="held-credit-banner"
              >
                <Wallet size={18} weight="duotone" className="text-[#2F6B3A] shrink-0 mt-0.5" />
                <div className="text-sm text-[#3A4A3E] leading-relaxed">
                  <strong>${(heldCents / 100).toFixed(2)} in platform credit is held and ready.</strong>{" "}
                  You topped up before creating a silo, so we're holding it safely — it drops straight
                  into your first silo the moment you create one. Nothing is lost.
                  {silos.length === 0 && (
                    <button
                      onClick={() => setShowCreate(true)}
                      data-testid="held-credit-create-silo"
                      className="ml-2 underline font-medium text-[#2F6B3A] hover:text-[#1C1B1A] transition-colors"
                    >
                      Create your first silo →
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Jul 2026 (Randy) — dashboard-top low AI-credit alert.
                Oct 2026 (Randy) — dismissible for the browser session. Gemini
                alone runs the whole service, so a permanent red bar about empty
                OpenAI and Claude pools nags about luxuries. Same contract as the
                resting-silos alert: gone for this session, back on next load. */}
            {lowSilos.length > 0 && !lowCreditDismissed && (
              <div
                className="mb-6 rounded-xl border border-[#C16E5A]/50 bg-[#FDF1ED] px-4 py-3.5 flex items-start gap-3"
                data-testid="low-credit-banner"
              >
                <Megaphone size={18} weight="fill" className="text-[#C16E5A] shrink-0 mt-0.5" />
                <div className="text-sm text-[#8A4B3B] leading-relaxed flex-1 min-w-0">
                  <strong>{lowSilos.length} silo{lowSilos.length === 1 ? "" : "s"} running low on AI credit.</strong>{" "}
                  {lowSilos.map((s) => s.name).slice(0, 4).join(", ")}{lowSilos.length > 4 ? "…" : ""}. Top up so answers, voice, and Silo Wars keep working — open the <strong>AI Credit</strong> chip on each card to see which pool is low.
                </div>
                <button
                  onClick={() => {
                    setLowCreditDismissed(true);
                    try { sessionStorage.setItem("mysilo_low_credit_dismissed", "1"); } catch { /* private mode */ }
                  }}
                  data-testid="low-credit-dismiss"
                  title="Hide until next time you load the dashboard"
                  aria-label="Dismiss"
                  className="text-[#C16E5A]/70 hover:text-[#8A4B3B] shrink-0 mt-0.5"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            )}

            {loading ? (
              <div className="text-[#6B6A68] text-sm">Loading…</div>
            ) : ownedSilos.length === 0 && collabSilos.length === 0 ? (
              <div className="bg-white border-2 border-dashed hairline rounded-xl p-12 text-center" data-testid="empty-silos">
                <Books size={36} weight="duotone" className="mx-auto text-[#4A5D4E]" />
                <h3 className="font-serif-display text-2xl mt-3 text-[#1C1B1A]">No silos yet.</h3>
                <p className="text-[#6B6A68] text-sm mt-2">Create your first one to start uploading and curating.</p>
                <button onClick={() => setShowCreate(true)} className="mt-6 px-5 py-3 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-2">
                  <Plus size={16} /> Create silo
                </button>
              </div>
            ) : (
              <>
              {ownedSilos.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="silos-grid">
                {ownedSilos.map((s, idx) => (
                  <SiloCubeCard key={s.id} silo={s} cubeEnabled={cubeEnabled} isFounder={user?.role === "super_admin"} onOpenStudio={() => openStudio(s)}
                    topRightBadge={
                      /* Feb 2026 pt89h (Randy) — tiny in-canvas alert dot.
                          Sits ABOVE the silo art (z-40), fully inside the pill
                          (top-right corner). Passed via topRightBadge prop so
                          it stays visible even when silo art is active — the
                          `children` container becomes invisible under art.
                          Feb 24, 2026 (Randy — YTRE alerts) — unified with
                          the new YTRE unread signal. Single dot, aggregate
                          count, tap opens the source directly OR a small
                          popover if BOTH sources have activity. */
                      <SiloAlertsDot silo={s}
                        choreCount={choreConfirmCounts[s.id] || 0}
                        ytreCount={ytreActivityCounts[s.id]?.unread_urls || 0}
                        ytreNewReplies={ytreActivityCounts[s.id]?.total_new_replies || 0}
                        onChoreCountChanged={(n) => setChoreConfirmCounts((c) => ({ ...c, [s.id]: n }))}
                        onYtreDismissed={(sid) => setYtreActivityCounts((c) => { const copy = { ...c }; delete copy[sid]; return copy; })} />
                    }
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">{s.template}</span>
                          {s.suspended && (
                            <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#FCEDE6] text-[#A85E4A] font-medium" data-testid={`silo-suspended-${s.slug}`}>
                              Suspended
                            </span>
                          )}
                          {/* Sprint Feb 8, 2026 — removed "N surfaces overridden"
                              diff badge per user request. The information lived
                              in the silo's Settings → Per-surface tune panel
                              already; the dashboard didn't need a duplicate
                              indicator that was just visually crowding the
                              card. Wiring left intact server-side
                              (silo.llm_override_count is still computed) so
                              we can surface it elsewhere later if needed. */}
                        </div>
                        <h3 className="font-serif-display text-xl sm:text-2xl mt-1 text-[#1C1B1A] flex items-center gap-2 flex-wrap">
                          <span>{s.name}</span>
                          <SiloKindPill silo={s} size="xs" />
                        </h3>
                        {pendingCounts[s.id] > 0 && (
                          <Link to={`/dashboard/silo/${s.id}`} data-testid={`approval-badge-${s.slug}`}
                            title="Waiting for your approval — open Staging"
                            className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-medium px-2 py-1 rounded-full bg-[#F8F1E2] border border-[#C19A4A] text-[#8A6A24] hover:bg-[#F3E7CB]">
                            <Tray size={11} weight="duotone" /> {pendingCounts[s.id]} waiting for approval
                          </Link>
                        )}
                        {s.tagline && <p className="text-xs sm:text-sm text-[#6B6A68] mt-1">{s.tagline}</p>}
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <div className="flex gap-1">
                          {/* Reorder controls — move silo up/down in the dashboard. Order persists across devices. */}
                          <button onClick={() => moveSilo(idx, -1)} disabled={idx === 0} title="Move up" data-testid={`silo-up-${s.slug}`}
                            className="p-2 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed">
                            <ArrowUp size={16} weight="bold" />
                          </button>
                          <button onClick={() => moveSilo(idx, 1)} disabled={idx === ownedSilos.length - 1} title="Move down" data-testid={`silo-down-${s.slug}`}
                            className="p-2 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed">
                            <ArrowDown size={16} weight="bold" />
                          </button>
                          <button onClick={() => onDelete(s)} title="Delete silo" data-testid={`delete-silo-${s.slug}`}
                            className="p-2 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]">
                            <Trash size={16} />
                          </button>
                        </div>
                        {/* Sprint Feb 8, 2026 — mobile-shrunk Copy button.
                            Desktop unchanged; mobile gets ~65% footprint via
                            tighter padding + 9px text + 10px icon. */}
                        <button
                          onClick={() => copyLink(s.slug)}
                          data-testid={`copy-link-${s.slug}`}
                          className="mt-1 text-[9px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.18em] px-1.5 py-1 sm:px-3 sm:py-1.5 rounded-md bg-[#F0F1EE] text-[#4A5D4E] hover:bg-[#E5E5E0] hover:text-[#1C1B1A] flex items-center gap-1 sm:gap-1.5 font-medium"
                        >
                          <Copy size={10} weight="bold" className="sm:hidden" />
                          <Copy size={12} weight="bold" className="hidden sm:inline-block" />
                          <span>{(s.silo_kind === "personal" || s.silo_kind === "organizer") ? "Copy private link" : "Copy public link"}</span>
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 sm:mt-5 flex flex-wrap gap-1.5 sm:gap-2">
                      {/* Jul 2026 (Randy) — AI Credit chip sits just above the
                          Manage/Hide/Open row. Auto-expands + reddens when a
                          voucher pool dips below 25¢. */}
                      <div className="w-full mb-1.5">
                        <SiloCreditChip
                          balances={creditBySilo[s.id] || {}}
                          byok={byokStatus}
                          ghost={(creditBySilo[s.id] || {}).ghost || {}}
                          testidSuffix={s.slug}
                          onTopUp={() => setCreditTopupSiloId(s.id)}
                        />
                      </div>
                      {/* Sprint Feb 8, 2026 — order swap: Manage (black, the
                          primary action) lives in the corner first; Hide is
                          the secondary toggle next to it; Open-public last. */}
                      <Link to={`/dashboard/silo/${s.id}`} data-testid={`manage-silo-${s.slug}`}
                        className="text-[10px] sm:text-sm px-2 py-1 sm:px-4 sm:py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1 sm:gap-1.5">
                        Manage <ArrowRight size={10} className="sm:hidden" /><ArrowRight size={14} className="hidden sm:inline-block" />
                      </Link>
                      {(s.silo_kind === "personal" || s.silo_kind === "organizer") ? (
                        <span
                          data-testid={`private-badge-${s.slug}`}
                          title={`${s.silo_kind === "organizer" ? "Organizer" : "Personal"} silo — private forever. It can never be made public.`}
                          className="text-[10px] sm:text-xs px-1.5 py-1 sm:px-2.5 sm:py-2 rounded-md flex items-center gap-1 font-medium bg-[#F0F1EE] text-[#4A5D4E] border border-[#4A5D4E]/20 cursor-default"
                        >
                          <EyeSlash size={10} weight="fill" className="sm:hidden" />
                          <EyeSlash size={12} weight="fill" className="hidden sm:inline-block" />
                          Private
                        </span>
                      ) : (
                      <button
                        type="button"
                        onClick={() => toggleHide(s)}
                        data-testid={`hide-silo-${s.slug}`}
                        title={s.is_hidden_from_public ? "Hidden from public — tap to make public again" : "Tap to hide from public detection"}
                        className={`text-[10px] sm:text-xs px-1.5 py-1 sm:px-2.5 sm:py-2 rounded-md flex items-center gap-1 font-medium transition-colors ${
                          s.is_hidden_from_public
                            ? "bg-[#FDF1ED] text-[#C16E5A] border border-[#C16E5A]/30 hover:bg-[#FCE8E0]"
                            : "border hairline text-[#6B6A68] hover:border-[#4A5D4E] hover:text-[#4A5D4E]"
                        }`}
                      >
                        <EyeSlash size={10} weight={s.is_hidden_from_public ? "fill" : "duotone"} className="sm:hidden" />
                        <EyeSlash size={12} weight={s.is_hidden_from_public ? "fill" : "duotone"} className="hidden sm:inline-block" />
                        Hide
                      </button>
                      )}
                      <a href={`/s/${s.slug}`} data-testid={`open-public-${s.slug}`}
                        className="text-[10px] sm:text-sm px-2 py-1 sm:px-4 sm:py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E]">
                        {(s.silo_kind === "personal" || s.silo_kind === "organizer") ? "Open private →" : "Open public →"}
                      </a>
                    </div>
                    {/* Bottom-right subscriber-count badge — populated by Stripe Connect (Phase 2). */}
                    <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5"
                      title={`${s.subscriber_count || 0} subscriber${(s.subscriber_count || 0) === 1 ? "" : "s"}`}
                      data-testid={`silo-subs-${s.slug}`}>
                      <Users size={12} weight="duotone" />
                      <span className="tabular-nums text-[#1C1B1A] font-medium">{(s.subscriber_count || 0).toLocaleString()}</span>
                      <span>sub{(s.subscriber_count || 0) === 1 ? "" : "s"}</span>
                    </div>
                  </SiloCubeCard>
                ))}
              </div>
              )}
              {collabSilos.length > 0 && (
              <div className="mt-12" data-testid="collab-silos-section">
                <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Collaborative silos.</h2>
                <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
                  Silos owned by others who&apos;ve invited you to help build. You can add to the library, refine, and shape the voice — the silo, its audience, its costs and earnings stay with the owner. (unless opted to split subscription earnings)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-testid="collab-silos-grid">
                  {collabSilos.map((s) => {
                    // Feb 2026 (Randy pivot) — for organizer collab silos the
                    // "Open" button routes to Family View first (not the
                    // traditional silo library). Users reach the library via
                    // the "Silo Library" button at the top of Family View.
                    // Feb 19, 2026 (Randy Tactic C) — ONLY when the silo is
                    // actually family-shaped (has kid profiles / parent pills
                    // / household PINs). Team-shaped organizer collabs
                    // (owner meant it for a small team, never set up family
                    // features) route to the traditional silo library so an
                    // adult friend/colleague isn't stuck behind Family View.
                    const isFamilyCollab = s.silo_kind === "organizer" && s.is_family_shaped;
                    const openPath = isFamilyCollab ? `/family-view/${s.id}` : `/dashboard/silo/${s.id}`;
                    return (
                    <SiloCubeCard key={s.id} silo={s} cubeEnabled={cubeEnabled} isFounder={user?.role === "super_admin"} onOpenStudio={() => openStudio(s)}
                      topRightBadge={
                        /* Feb 2026 (Randy) — pending-confirmation dot on collab
                           organizer silos too, for parent-role members. Tapping
                           the card goes to Family View where they can approve.
                           Feb 19, 2026 (Randy) — only when family-shaped; team-
                           shaped organizer silos never generate chore approvals.
                           Feb 24, 2026 (Randy — YTRE alerts) — unified with
                           YTRE unread signal. Same rule applies here: only
                           render for family-shaped organizer collabs. */
                        isFamilyCollab && ((choreConfirmCounts[s.id] || 0) > 0 || (ytreActivityCounts[s.id]?.unread_urls || 0) > 0) ? (
                          <SiloAlertsDot silo={s}
                            choreCount={choreConfirmCounts[s.id] || 0}
                            ytreCount={ytreActivityCounts[s.id]?.unread_urls || 0}
                            ytreNewReplies={ytreActivityCounts[s.id]?.total_new_replies || 0}
                            onChoreCountChanged={(n) => setChoreConfirmCounts((c) => ({ ...c, [s.id]: n }))}
                            onYtreDismissed={(sid) => setYtreActivityCounts((c) => { const copy = { ...c }; delete copy[sid]; return copy; })} />
                        ) : null
                      }
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">{s.template}</span>
                            <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#E8EBE5] text-[#4A5D4E] font-medium" data-testid={`silo-collab-badge-${s.slug}`}>
                              {isFamilyCollab ? "Family collaboration" : (s.silo_kind === "organizer" ? "Team collaboration" : "Collaboration")}
                            </span>
                          </div>
                          <h3 className="font-serif-display text-xl sm:text-2xl mt-1 text-[#1C1B1A]">{s.name}</h3>
                          {s.tagline && <p className="text-xs sm:text-sm text-[#6B6A68] mt-1">{s.tagline}</p>}
                        </div>
                        <button
                          onClick={() => copyLink(s.slug)}
                          data-testid={`copy-link-${s.slug}`}
                          className="mt-1 text-[9px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.18em] px-1.5 py-1 sm:px-3 sm:py-1.5 rounded-md bg-[#F0F1EE] text-[#4A5D4E] hover:bg-[#E5E5E0] hover:text-[#1C1B1A] flex items-center gap-1 sm:gap-1.5 font-medium shrink-0"
                        >
                          <Copy size={12} weight="bold" />
                          <span>Copy link</span>
                        </button>
                      </div>
                      <div className="mt-3 sm:mt-5 flex flex-wrap gap-1.5 sm:gap-2">
                        <Link to={openPath} data-testid={`manage-silo-${s.slug}`}
                          className="text-[10px] sm:text-sm px-2 py-1 sm:px-4 sm:py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1 sm:gap-1.5">
                          {isFamilyCollab ? "Open family view" : "Open"} <ArrowRight size={14} className="hidden sm:inline-block" />
                        </Link>
                        <a href={`/s/${s.slug}`} data-testid={`open-public-${s.slug}`}
                          className="text-[10px] sm:text-sm px-2 py-1 sm:px-4 sm:py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E]">
                          {(s.silo_kind === "personal" || s.silo_kind === "organizer") ? "Open private →" : "Open public →"}
                        </a>
                      </div>
                      <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5" data-testid={`silo-subs-${s.slug}`}>
                        <Users size={12} weight="duotone" />
                        <span className="tabular-nums text-[#1C1B1A] font-medium">{(s.subscriber_count || 0).toLocaleString()}</span>
                        <span>sub{(s.subscriber_count || 0) === 1 ? "" : "s"}</span>
                      </div>
                    </SiloCubeCard>
                    );
                  })}
                </div>
              </div>
              )}
              </>
            )}
            {/* May 25, 2026 — New-LLM-detected banner. Sits at the bottom of
                "Your Silos" so creators see model-release news right where
                they manage the silos that would use them. Empty when no
                releases are tracked. */}
            <NewLLMBanner />
          </div>
          <div className={activeTab === "users" ? "" : "hidden"} data-testid="dashboard-pane-users">
            <UsersTab scope="me" />
          </div>

          {/* === EARNINGS TAB (iter-35b) === */}
          <div className={activeTab === "earnings" ? "" : "hidden"} data-testid="dashboard-pane-earnings">
            <EarningsDashboard onTopUp={() => setTopUpOpen(true)} />
          </div>

          {/* === PROFILE TAB (Feb 18, 2026) === Site name + Public profile
              moved here from Account & Billing. They're identity-level,
              not billing-level — they belong with the creator's other
              "who am I on the platform" controls. */}
          <div className={activeTab === "profile" ? "" : "hidden"} data-testid="dashboard-pane-profile">
            <div className="mb-6">
              <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Your profile.</h2>
              <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
                How you show up on the platform. Your site name is the label fans see
                ("Invited by [name]"). Your public profile is what people see when they
                search for you or land on /u/your-handle.
              </p>
            </div>
            <div className="space-y-6 max-w-3xl">
              <SiteNameCard />
              <ProfileCard />
            </div>
          </div>

          {/* === INTERESTS TAB === */}
          <div className={activeTab === "interests" ? "" : "hidden"} data-testid="dashboard-pane-interests">
            <div className="mb-6">
              <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Your interests.</h2>
              <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
                These shape what you see in Discover across the platform — matching silos surface first, then the rest fill in. They came with you from your fan days and travel with your account.
              </p>
            </div>
            <div className="max-w-3xl">
              <InterestsPicker />
            </div>
          </div>

          {/* === OPERATIONS TAB === */}
          <div className={activeTab === "ops" ? "" : "hidden"} data-testid="dashboard-pane-ops">
            <div className="mb-6">
              <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Operations & telemetry.</h2>
              <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
                The back-office. See exactly what your silos cost, monitor the cache hit ratio, and control the LLM key that fuels every answer.
                Everything you need to run a sustainable AI twin business without surprises on the bill.
              </p>
            </div>

            {/* Jun 2026 — Operations reordered per Randy's screenshot to the
                reading order he marked:
                  1) Provider balances (ProviderTrackersCard)
                  2) Usage overview
                  3) Subscription vs LLM breakdown (PlatformKeyInfoCard)
                The all-silos public Q&A model banner was removed here (Jun 23)
                — it lives per-silo in each silo's Ops tab, which is enough. */}

            {/* 2 — Your provider balances · BYOK direct */}
            <div className="mb-6" data-testid="dashboard-provider-trackers-row">
              <ProviderTrackersCard />
            </div>

            {/* 3 — Usage overview / report */}
            <div className="mb-6" data-testid="dashboard-billing-row">
              <UsageOverview key={`me-${usageVersion}`} scope="me" onTopUp={() => setTopUpOpen(true)} />
            </div>

            {/* 4 — Your subscription vs LLM usage — the real breakdown.
                Keep the BYOK nudge banner with it since both are about
                connecting your own keys to save. */}
            <BYOKPromoBanner user={me} />
            <div className="mb-6" data-testid="dashboard-platform-info-row">
              <PlatformKeyInfoCard onTopUp={() => setTopUpOpen(true)} />
            </div>

            {/* Feb 27, 2026 — Phase 2A. ONE unified BYOK panel for all three
                providers (Gemini, Anthropic, OpenAI). Replaces the old
                standalone Gemini BYOKCard + the separate Claude/GPT panel
                with a single coherent "connect your direct provider
                accounts" section. */}
            <div className="mb-6" data-testid="dashboard-provider-byok-row">
              <CreatorProviderByokPanel user={me} onUpdated={(u) => setMe(u)} />
            </div>

            <div className="mb-6" data-testid="dashboard-youtube-key-row">
              <YouTubeKeyCard user={me} onUpdated={(u) => setMe(u)} />
            </div>

            <div className="mb-6">
              <CostCacheDiag key={`diag-${usageVersion}`} />
            </div>

            <div className="mb-6">
              <CreatorLLMRoutingCard />
            </div>
          </div>

          {/* === COMMUNICATIONS TAB === */}
          {/* Order per Feb 17, 2026 spec: Compose broadcast → Custom marketing
              emails → Silo marketing links → Platform email catalog.
              Feb 19, 2026 — silo_links gated to affiliates only (referral
              link generator). Non-affiliates see an upsell card instead. */}
          <div className={activeTab === "comms" ? "" : "hidden"} data-testid="dashboard-pane-comms">
            <div className="space-y-8">
              <CommunicationsPanel isAdmin={false} section="compose" />
              <MarketingPanel section="templates" />
              {(me?.is_affiliate || me?.role === "super_admin") ? (
                <MarketingPanel section="silo_links" />
              ) : (
                <section data-testid="silo-links-upsell-card" className="bg-gradient-to-br from-[#FDFCF8] to-[#F0F1EE] border-2 border-dashed hairline rounded-xl p-6">
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-12 h-12 rounded-md bg-white border hairline flex items-center justify-center shrink-0">
                      <Megaphone size={22} weight="duotone" className="text-[#C16E5A]" />
                    </div>
                    <div className="flex-1 min-w-[260px]">
                      <h3 className="font-serif-display text-lg text-[#1C1B1A]">Trackable referral links — Creator Pro only.</h3>
                      <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
                        Generate per-silo share links with built-in attribution, so every signup that lands through your link is locked to you. Upgrade to Creator Pro to unlock the trackable link generator.
                      </p>
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => switchTab("earnings")}
                          data-testid="silo-links-upsell-cta"
                          className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5"
                        >
                          See the upgrade <ArrowRight size={12} weight="bold" />
                        </button>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
                          Lives in your Earnings tab
                        </span>
                      </div>
                    </div>
                  </div>
                </section>
              )}
              <CommunicationsPanel isAdmin={false} section="catalog" />
            </div>
          </div>

          {/* === HOW IT WORKS / FAQ TAB (v2 — full refresh, search-first) === */}
          <div className={activeTab === "faq" ? "" : "hidden"} data-testid="dashboard-pane-faq">
            <HowItWorksFAQv2 />
          </div>
        </section>
      </main>

      <CreateSiloDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={(s, kind) => {
        setSilos((p) => [s, ...p]);
        // Jul 2026 (Randy) — Step-0 silo types pop the full toolkit buffet with
        // the matching SKUs pre-selected (gate provider is app-level, so the
        // modal survives the navigation to the new silo's manage page).
        if (kind === "twin") cpOpenGate(["min_workbench", "youtube_twin", "macro_autobuild"]);
        else if (kind === "personal") cpOpenGate(["staging", "ai_calendar"]);
        nav(`/dashboard/silo/${s.id}`);
      }} />
      <TopUpModal
        open={topUpOpen}
        currentBalanceCents={me?.credit_balance_cents || 0}
        onClose={() => setTopUpOpen(false)}
        onSuccess={(u) => { setMe(u); setUsageVersion((v) => v + 1); }}
      />
      {studioSilo && (
        <SiloImageStudio
          silo={studioSilo}
          open={!!studioSilo}
          isFounder={me?.role === "super_admin"}
          onClose={() => setStudioSilo(null)}
          onSaved={() => load()}
        />
      )}
      {/* Jul 2026 (Randy) — per-silo AI Credit top-up (from the card chip). */}
      <VoucherTopupModal
        open={!!creditTopupSiloId}
        prefocusedSiloId={creditTopupSiloId}
        onClose={() => setCreditTopupSiloId(null)}
        onSuccess={() => { setCreditTopupSiloId(null); load(); }}
      />
    </div>
  );
}


// =====================================================================
// SiteNameInline — Sprint Feb 8, 2026
// Quick edit-in-place site-name field on the dashboard. The same field
// also lives in Account Settings; this is just an ergonomic shortcut.
// PencilSimple → tap → input + Save. Empty saves clear it (falls back
// to user.name on the public silo "Invited by" pill).
// =====================================================================
function SiteNameInline() {
  const [siteName, setSiteName] = useState("");
  const [snapshot, setSnapshot] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get("/me/site-name")
      .then((r) => { if (!cancel) { setSiteName(r.data?.site_name || ""); setSnapshot(r.data?.site_name || ""); setLoaded(true); } })
      .catch(() => { if (!cancel) setLoaded(true); });
    return () => { cancel = true; };
  }, []);

  const trimmed = siteName.trim();
  const fallback = (getUser()?.name || "").trim();
  const display = trimmed || fallback || "—";

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/me/site-name", { site_name: trimmed || null });
      const v = r.data?.site_name || "";
      setSiteName(v);
      setSnapshot(v);
      setEditing(false);
      toast.success(trimmed ? "Site name saved." : "Site name cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save site name.");
    } finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <div className="mt-3 inline-flex items-center gap-2 flex-wrap" data-testid="dashboard-site-name-inline">
      {/* Iter160m (Randy pt-siloart-loosetext-sweep, Feb 2026) — dropped
          `!text-black` from the "SITE NAME" label. It sits LOOSE on the
          dashboard canvas (no card behind it) and Randy expects it to
          obey Silo Art V2 white/black text rules like every other loose
          label. The <button> keeps its !text-black because buttons are
          structurally excluded from the loose-text rule (per the
          codified guideline in index.css). */}
      <span className="text-[10px] uppercase tracking-[0.22em] silo-loose">Site name</span>
      {!editing ? (
        <button
          onClick={() => setEditing(true)}
          data-testid="dashboard-site-name-edit"
          title="Click to edit your platform handle (shown in 'Invited by …' across My Silo)"
          className="text-sm font-medium !text-black underline decoration-dotted underline-offset-4 hover:decoration-solid"
        >
          {display}
        </button>
      ) : (
        <>
          <input
            type="text"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value.slice(0, 40))}
            placeholder="e.g. Cosmic"
            disabled={saving}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setSiteName(snapshot); setEditing(false); } }}
            data-testid="dashboard-site-name-input"
            className="text-sm px-2 py-1 border hairline rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] w-44"
            maxLength={40}
          />
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="dashboard-site-name-save"
            className="text-xs px-2.5 py-1 rounded bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => { setSiteName(snapshot); setEditing(false); }}
            disabled={saving}
            className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
}



